import './ExternalInvestmentProductCardTable.scss';

import { camelCase, kebabCase, isEmpty, isFinite, isNil, some } from 'lodash';
import React, { Fragment } from 'react';
import { arrayOf, object } from 'prop-types';

import { Dollar, FormattedDateTime } from 'components/shared/Text/Text';
import { ReactComponent as WarningImage } from 'assets/icons/warning-triangle.svg';
import { externalAccountErrorMap } from 'services/ExternalAccountErrorCodes';
import Time from 'services/Time';
import { UpdateAccountError } from 'services/ExternalAccountErrorCodes';

const externalInvestmentProductLabels = ['Account', 'Balance'];

let allExternalAccountsInGroupError = false;

const ExternalInvestmentProductHeaders = ({ columns = [] }) => (
  <tr className="columns">
    {columns.map((name) => (
      <th
        scope="col"
        className={`external-investment-product-header-cell test-${camelCase(
          name
        )} external-investment-product-${kebabCase(name)}`}
        key={name}>
        {name}
      </th>
    ))}
  </tr>
);

const Account = ({ account }) => {
  let errorMessage = '';
  const accountLevelUpdateDateError =
    new Date(account.lastSuccessfulUpdateDate) < Time.daysBeforeNow(90);

  if (
    externalAccountErrorMap &&
    externalAccountErrorMap.has(account.accountStatus)
  ) {
    const { message } = externalAccountErrorMap
      ? externalAccountErrorMap.get(account.accountStatus)
      : {};
    errorMessage = message;
  } else if (accountLevelUpdateDateError) {
    errorMessage = <UpdateAccountError />;
  }

  return (
    <td className="account-group">
      <div className="account-name">
        {!allExternalAccountsInGroupError && errorMessage && <WarningImage />}
        {account.accountName} {`- ${account.accountNumber}`}
        <p className="account-group-as-of-date">
          As of{' '}
          <FormattedDateTime>
            {' '}
            {account.lastSuccessfulUpdateDate}
          </FormattedDateTime>{' '}
          ET
        </p>
        {!allExternalAccountsInGroupError && errorMessage}
      </div>
    </td>
  );
};

const Balance = ({ balance: { balanceValue, accountStatus } = {} }) =>
  accountStatus === 0 && isFinite(balanceValue) ? (
    <Dollar element="td" className="balance-group e2e-balance-group">
      {balanceValue}
    </Dollar>
  ) : (
    <td className="balance-group currently-unavailable">
      Currently Unavailable
    </td>
  );

const RowCells = ({ rowData, columns }) =>
  columns.map((colName) => {
    const col = camelCase(colName);
    const CellComponent = (props) =>
      cellComponentMapper[col](...Object.values(props)); // This allows us to set a key on each cell without requiring it in the mapper
    const cellBody = isNil(rowData[col]) ? '' : rowData[col]; // 0 is a valid body, isNil will catch null and undefined, empty objects will be handle by their cell component
    return (
      <CellComponent key={colName} cellBody={cellBody} rowData={rowData} />
    );
  });

const TableHeaderRow = ({
  balanceValue,
  name,
  validBalances,
  errorMessage
}) => (
  <tr className="row-parent">
    <td className="account-header-group e2e-group-name">
      <div className="parent-account-type-label">
        {errorMessage ? (
          <div>
            <WarningImage />
            {name.groupLabel}
            {errorMessage}
          </div>
        ) : (
          <div>{name.groupLabel}</div>
        )}
      </div>
    </td>
    <Balance
      balance={{ balanceValue, accountStatus: validBalances ? 0 : null }}
    />
  </tr>
);

const ExternalInvestmentProductRows = ({ columns = [], rows = [] }) =>
  rows.map((row, key) => (
    <tr className="row-child" key={key}>
      <RowCells rowData={row.details} columns={columns} />
    </tr>
  ));

const cellComponentMapper = {
  account: (account) => <Account account={account} />,
  balance: (balance) => <Balance balance={balance} />
};

const isValidBalance = (b) =>
  b.details.balance.accountStatus === 0 &&
  isFinite(b.details.balance.balanceValue);
const hasValidBalances = (balances) => some(balances, isValidBalance);

const ExternalInvestmentProductCardTable = ({ balances }) => {
  return (
    <div className="external-investment-product-card-table-container">
      {!isEmpty(balances) && (
        <table className="external-investment-product-card-table">
          <thead className="external-investment-product-card-header">
            <ExternalInvestmentProductHeaders
              columns={externalInvestmentProductLabels}
            />
          </thead>
          <tbody>
            {balances.map((institutionGroup, i) => {
              const headerRow = institutionGroup;
              const balanceRows = institutionGroup.children;
              allExternalAccountsInGroupError =
                headerRow.details.allExternalAccountsInGroupError;

              return (
                <Fragment key={i}>
                  <TableHeaderRow
                    {...headerRow.details}
                    validBalances={hasValidBalances(balanceRows)}
                  />
                  <ExternalInvestmentProductRows
                    rows={balanceRows}
                    columns={externalInvestmentProductLabels}
                  />
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

ExternalInvestmentProductCardTable.propTypes = {
  balance: arrayOf(object)
};

export default ExternalInvestmentProductCardTable;
